import React from "react";
import { formatAmountWithCurrency, getOrderShopName } from "../../utils";
import InfoBox, { IconFn } from "./InfoBox.jsx";

import { faClock, faCloud, faLandmark, faMoneyBill, faMoneyBillTransfer, faStore, faTshirt } from "@fortawesome/pro-regular-svg-icons";
import { formatReadableDateAndTime } from "rylak-common";
import { TableCellReturnItems } from "../table/cells/TableCellReturnItems.jsx";
import TurnrOrderLink from "../links/TurnrOrderLink.jsx";
import { getOrderCurrencyCode, getOrderId, getOrderNumberOfItems } from "~/cores/orderCore";
import { OpenOrderInShopButton } from "../ecom/OpenOrderInEcomButton.jsx";
import isNil from "lodash/isNil";

export default function OrderInformationInfoBox({ appProps, order, highlightText }) {
    return (
        <InfoBox
            size={"small"}
            items={[
                {
                    iconFn: () => <IconFn icon={<OpenOrderInShopButton width={30} placement="left" order={order} iconSize={"xs"} />} />,
                    label: "Foreign ID",
                    value: getOrderId(order),
                    renderValueFn: () => {
                        return <TurnrOrderLink order={order} highlightText={highlightText} showShopButton={false} />;
                    },
                },
                {
                    icon: faClock,
                    label: "Created (UTC)",
                    value: order?.shopOrderCreatedAt && formatReadableDateAndTime({ dateTime: order?.shopOrderCreatedAt }),
                },
                {
                    icon: faTshirt,
                    label: "Order items",
                    value: getOrderNumberOfItems(order),
                    renderValueFn: (value) => {
                        // TableCellReturnItems hides its own content when the order has no items, which would
                        // leave the icon standing alone — spell the empty count out in the cell's own wording.
                        return value ? (
                            <div style={{ width: "fit-content" }}>
                                <TableCellReturnItems appProps={appProps} order={order} renderAsDiv />
                            </div>
                        ) : (
                            `${value} items`
                        );
                    },
                },
                {
                    icon: faMoneyBill,
                    label: "Order value",
                    value: order?.totalAmountCustomer,
                    renderValueFn: (value) => {
                        return formatAmountWithCurrency(value, { currencyCode: getOrderCurrencyCode(order) });
                    },
                },
                {
                    icon: faMoneyBillTransfer,
                    label: "Original order value",
                    value: order?.parentOrder?.totalAmountCustomer,
                    renderValueFn: (value) => {
                        // The parent is a partial entity, so fall back to this order's currency rather than
                        // letting formatAmountWithCurrency hand back a bare, unlabelled number.
                        return formatAmountWithCurrency(value, { currencyCode: getOrderCurrencyCode(order?.parentOrder) || getOrderCurrencyCode(order) });
                    },
                },
                {
                    icon: faLandmark,
                    label: "Shop group",
                    value: order?.merchantBrand?.merchant?.name,
                },
                {
                    icon: faStore,
                    label: "Shop",
                    value: getOrderShopName(order),
                },
                {
                    icon: faCloud,
                    label: "Sales platform",
                    value: order?.merchantSalesPlatform?.name,
                },
                // A legitimate 0 — an exchange order's value, an order with no line items — is worth showing,
                // so drop rows on nil/empty rather than on falsy.
            ].filter(({ value }) => !isNil(value) && value !== "")}
        />
    );
}
